/**
 * VersionDistributionCard — AdminAppVersion.js için sürüm dağılım bölümü
 *
 * Yerleşim (bkz. AdminAppVersion.js): kırmızı min-version kilit uyarısının
 * ALTINA, "Opsiyonel Güncelleme Mesajı Override'ı" bölümünün ÜSTÜNE tek bir
 * FormSection olarak yerleştirilir.
 *
 * Veri kaynağı: GET /admin/app-version/stats?days=7|30 (bkz.
 * backend/routes/app_version.py) — bu kart kendi loading/error durumunu
 * yönetmez, PARENT (AdminAppVersion.js) stats fetch'ini yapıp sonucu prop
 * olarak geçer (sayfa geneli config loading/error'dan BAĞIMSIZ ayrı bir
 * veri kaynağı — configs yüklenmiş olsa bile stats hâlâ yükleniyor olabilir).
 *
 * Görselleştirme kasıtlı olarak METİN öncelikli: her satırda sürüm, istek
 * sayısı, yüzde ve gün bilgisi DÜZ METİN olarak yazılır (WCAG 1.4.1); bar
 * sadece dekoratif görsel destektir, aria-hidden="true" — aynı bilgi bar'a
 * ayrıca aria-label ile YAZILMAZ (çift okutma önlenir).
 */
import PropTypes from 'prop-types';
import { AlertCircle, BarChart3, Info, Smartphone, TrendingUp } from 'lucide-react';
import FormSection from './FormSection';
import EmptyState from './EmptyState';
import LoadingState from './LoadingState';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { fmtDateOnly } from '../../utils/formatDateOnly';

const MAX_VISIBLE_ROWS = 5;
const GROUP_THRESHOLD = 6;
const MAX_VISIBLE_DATES = 5;

function VersionRow({ version, requests, percent, daysSeen, windowDays, isConfigured, isObservedMax, t, platform }) {
  return (
    <li
      className="py-2 first:pt-0 last:pb-0"
      data-testid={`app-version-distribution-row-${platform}-${version}`}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <span className="font-mono font-bold text-[var(--text-main)]">{version}</span>
        {isConfigured && (
          <span className="inline-flex items-center rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-600">
            {t('admin.appVersion.configuredBadge')}
          </span>
        )}
        {isObservedMax && (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
            <TrendingUp size={12} />
            {t('admin.appVersion.observedMaxBadge')}
          </span>
        )}
        <span className="ml-auto text-[var(--text-muted)]">
          {t('admin.appVersion.totalRequests').replace('{{count}}', requests.toLocaleString())}
        </span>
        <span className="font-bold text-[var(--text-main)] w-14 text-right">{percent}%</span>
        <span className="text-xs text-[var(--text-muted)]">
          {t('admin.appVersion.daysSeenLabel').replace('{{days}}', daysSeen).replace('{{window}}', windowDays)}
        </span>
      </div>
      <div
        className="mt-1.5 h-2 w-full rounded-full bg-[var(--bg-app)] overflow-hidden"
        aria-hidden="true"
      >
        <div
          className="h-full rounded-full bg-cyan-500"
          style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
        />
      </div>
    </li>
  );
}

function StaleWarningCallout({ warning, onFillSuggested, filled, platform, t }) {
  if (!warning) return null;

  const observedVersion = warning.observed_max_version;
  const percent = warning.observed_max_percent;
  const days = warning.observed_max_days_seen;
  const requests = warning.observed_max_requests;
  const configuredVersion = warning.configured_latest_version;

  if (warning.level === 'confirmed') {
    return (
      <div
        className="mt-4 flex items-start gap-3 rounded-2xl border-2 border-amber-200 bg-amber-50 p-4"
        role="status"
        aria-live="polite"
        data-testid={`app-version-stale-warning-${platform}`}
      >
        <TrendingUp size={20} className="mt-0.5 flex-shrink-0 text-amber-600" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-amber-700">{t('admin.appVersion.staleConfirmedTitle')}</p>
          <p className="mt-0.5 text-xs text-amber-600">
            {t('admin.appVersion.staleConfirmedBody')
              .replace('{{observedVersion}}', observedVersion)
              .replace('{{percent}}', String(percent))
              .replace('{{days}}', String(days))
              .replace('{{requests}}', requests.toLocaleString())
              .replace('{{configuredVersion}}', configuredVersion)}
          </p>
          <div className="mt-2.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-amber-400 text-amber-700 hover:bg-amber-100 hover:text-amber-800"
              onClick={() => onFillSuggested(observedVersion)}
              data-testid={`app-version-stale-fill-${platform}`}
            >
              {t('admin.appVersion.staleConfirmedFillCta')}
            </Button>
            {filled && (
              <p className="mt-1.5 text-xs font-semibold text-amber-600">
                {t('admin.appVersion.staleFillPendingHint')}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mt-4 flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 p-3"
      role="status"
      aria-live="polite"
      data-testid={`app-version-stale-warning-${platform}`}
    >
      <Info size={16} className="mt-0.5 flex-shrink-0 text-sky-600" />
      <p className="text-xs text-sky-700">
        {t('admin.appVersion.staleHintBody')
          .replace('{{observedVersion}}', observedVersion)
          .replace('{{percent}}', String(percent))
          .replace('{{days}}', String(days))}
      </p>
    </div>
  );
}

// Cardinality cap (backend: günde platform başına en fazla 40 distinct sürüm
// telemetriye yazılır) doldu mu — dolduysa o günün dağılımı eksik olabilir.
// Bu bir hata/aksiyon uyarısı DEĞİL, sadece bir veri kalitesi notu — bu yüzden
// StaleWarningCallout'un (rose/amber/sky KUTU'ları) aksine kutu/arkaplan
// KULLANMAZ; sadece ince bir üst ayraçla (hairline) ayrılan düz metin notu.
// Böylece hemen üstünde aynı sky tonunda duran "hint" callout'uyla (bir kutu)
// görsel olarak karışmaz — biri kutu, diğeri dipnot.
function DataQualityNote({ dataQuality, platform, lang, t }) {
  if (!dataQuality || !dataQuality.cap_hit_days) return null;

  const dates = dataQuality.cap_hit_dates || [];
  const localizedDates = dates.map((d) => fmtDateOnly(d, lang) || d);
  const visibleDates = localizedDates.slice(0, MAX_VISIBLE_DATES);
  const extraCount = localizedDates.length - visibleDates.length;
  const datesLabel = extraCount > 0
    ? `${visibleDates.join(', ')} +${extraCount}`
    : visibleDates.join(', ');

  return (
    <div
      className="mt-3 flex items-start gap-1.5 border-t border-[var(--border-light)] pt-2.5"
      data-testid={`app-version-data-quality-${platform}`}
    >
      <AlertCircle size={14} className="mt-0.5 flex-shrink-0 text-sky-700" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-sky-700">
          {t('admin.appVersion.dataQualityCapHit')
            .replace('{{days}}', String(dataQuality.cap_hit_days))
            .replace('{{max}}', String(dataQuality.max_versions_per_day))}
        </p>
        {datesLabel && (
          <p className="mt-0.5 text-xs text-sky-700">
            {t('admin.appVersion.dataQualityCapHitDates').replace('{{dates}}', datesLabel)}
          </p>
        )}
      </div>
    </div>
  );
}

export default function VersionDistributionCard({
  platform,
  windowDays,
  onWindowChange,
  fromDate,
  toDate,
  loading,
  error,
  onRetry,
  data,
  onFillSuggested,
  fillPending,
  t,
  lang,
}) {
  const windowToggle = (
    <Tabs value={String(windowDays)} onValueChange={(v) => onWindowChange(Number(v))}>
      <TabsList className="h-8 rounded-lg border-2 border-[var(--border-light)] bg-white gap-1 p-0.5">
        <TabsTrigger
          value="7"
          className="rounded-md px-2.5 py-1 text-xs font-bold data-[state=active]:bg-[var(--brand-primary)] data-[state=active]:text-white"
          data-testid="app-version-stats-window-7"
        >
          {t('admin.appVersion.window7d')}
        </TabsTrigger>
        <TabsTrigger
          value="30"
          className="rounded-md px-2.5 py-1 text-xs font-bold data-[state=active]:bg-[var(--brand-primary)] data-[state=active]:text-white"
          data-testid="app-version-stats-window-30"
        >
          {t('admin.appVersion.window30d')}
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );

  const description = `${t('admin.appVersion.sectionDistributionDesc').replace('{{days}}', windowDays)}${
    fromDate && toDate
      ? ` ${t('admin.appVersion.windowRange').replace('{{from}}', fromDate).replace('{{to}}', toDate)}`
      : ''
  }`;

  let body = null;

  if (loading) {
    body = <LoadingState variant="bars" count={6} />;
  } else if (error) {
    body = (
      <EmptyState
        icon={<BarChart3 size={40} strokeWidth={1.5} />}
        title={t('admin.appVersion.distributionLoadError')}
        action={{ label: t('admin.appVersion.retry'), onClick: onRetry }}
      />
    );
  } else if (!data || data.total_requests === 0) {
    body = (
      <EmptyState
        icon={<Smartphone size={40} strokeWidth={1.5} />}
        title={t('admin.appVersion.distributionEmptyTitle')}
        description={t('admin.appVersion.distributionEmptyBody')}
      />
    );
  } else {
    const versions = data.versions || [];
    const configuredLatest = data.configured_latest_version;
    const observedMaxVersion = data.stale_latest_warning?.observed_max_version || null;
    const shouldGroup = versions.length > GROUP_THRESHOLD;
    const visible = shouldGroup ? versions.slice(0, MAX_VISIBLE_ROWS) : versions;
    const rest = shouldGroup ? versions.slice(MAX_VISIBLE_ROWS) : [];
    const restRequests = rest.reduce((sum, v) => sum + v.requests, 0);
    const restPercent = rest.reduce((sum, v) => sum + v.percent, 0);

    body = (
      <>
        <p className="mb-3 text-xs font-semibold text-[var(--text-muted)]">
          {t('admin.appVersion.totalRequests').replace('{{count}}', data.total_requests.toLocaleString())}
        </p>
        <ul className="divide-y divide-[var(--border-light)]">
          {visible.map((v) => (
            <VersionRow
              key={v.version}
              version={v.version}
              requests={v.requests}
              percent={v.percent}
              daysSeen={v.days_seen}
              windowDays={windowDays}
              isConfigured={v.version === configuredLatest}
              isObservedMax={v.version === observedMaxVersion}
              t={t}
              platform={platform}
            />
          ))}
          {shouldGroup && (
            <li className="py-2 last:pb-0">
              <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm">
                <span className="font-semibold text-gray-600">
                  {t('admin.appVersion.otherVersionsLabel').replace('{{count}}', rest.length)}
                </span>
                <span className="ml-auto text-gray-500">
                  {t('admin.appVersion.totalRequests').replace('{{count}}', restRequests.toLocaleString())}
                </span>
                <span className="w-14 text-right font-semibold text-gray-600">
                  {Math.round(restPercent * 100) / 100}%
                </span>
              </div>
            </li>
          )}
        </ul>
        <StaleWarningCallout
          warning={data.stale_latest_warning}
          onFillSuggested={onFillSuggested}
          filled={fillPending}
          platform={platform}
          t={t}
        />
        <DataQualityNote
          dataQuality={data.data_quality}
          platform={platform}
          lang={lang}
          t={t}
        />
      </>
    );
  }

  return (
    <FormSection
      title={t('admin.appVersion.sectionDistribution')}
      description={description}
      columns={1}
      actions={windowToggle}
    >
      <div>{body}</div>
    </FormSection>
  );
}

VersionDistributionCard.propTypes = {
  platform: PropTypes.oneOf(['android', 'ios']).isRequired,
  windowDays: PropTypes.oneOf([7, 30]).isRequired,
  onWindowChange: PropTypes.func.isRequired,
  fromDate: PropTypes.string,
  toDate: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.bool,
  onRetry: PropTypes.func,
  data: PropTypes.shape({
    total_requests: PropTypes.number,
    configured_latest_version: PropTypes.string,
    versions: PropTypes.array,
    stale_latest_warning: PropTypes.object,
    data_quality: PropTypes.shape({
      max_versions_per_day: PropTypes.number,
      cap_hit_days: PropTypes.number,
      cap_hit_dates: PropTypes.arrayOf(PropTypes.string),
    }),
  }),
  onFillSuggested: PropTypes.func,
  fillPending: PropTypes.bool,
  t: PropTypes.func.isRequired,
  lang: PropTypes.string,
};
